export type AnalyticsSink = (
  name: string,
  params?: Record<string, unknown>,
) => void;

export interface AnalyticsBuffer {
  log: (name: string, params?: Record<string, unknown>) => void;
  attach: (sink: AnalyticsSink) => void;
  disable: () => void;
}

type QueuedEvent = { name: string; params?: Record<string, unknown> };

/**
 * Buffers analytics events until the SDK finishes initialising.
 *
 * Firebase Analytics init is async (isSupported -> getAnalytics), so events
 * fired during startup — anonymous_sign_in, the first screen_view — would
 * otherwise hit a null instance and vanish. Those are exactly the events a
 * sign-up funnel starts from.
 */
export function createAnalyticsBuffer(maxQueued = 50): AnalyticsBuffer {
  let sink: AnalyticsSink | null = null;
  let disabled = false;
  const queue: QueuedEvent[] = [];

  return {
    log(name, params) {
      if (sink) {
        sink(name, params);
        return;
      }
      if (disabled) return;
      // Bounded: if init never resolves, keep the most recent events rather
      // than growing without limit.
      if (queue.length >= maxQueued) queue.shift();
      queue.push({ name, params });
    },

    attach(nextSink) {
      if (disabled) return;
      sink = nextSink;
      queue.splice(0).forEach((event) => nextSink(event.name, event.params));
    },

    disable() {
      disabled = true;
      queue.length = 0;
    },
  };
}
